import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LayerGroup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { ICAS_PANE, ensureIcasPane } from './pane';
import type { IcasFeatureProperties } from './types';

/**
 * Configuration for a PMTiles-backed ICAS overlay. We currently bake two:
 *
 *   stands.pmtiles   — 89,635 polygons, species inventory, ~84 MB
 *   trupuri.pmtiles  — 3,601 polygons, forest-body boundaries, ~5 MB
 *
 * Both ride the same `protomaps-leaflet` runtime, so we share one
 * component and parameterise the differences (URL, vector-tile layer
 * name, paint colors, popup builder).
 */
export type IcasPmtilesConfig = {
  /** Identifier; used only for log/debug, not parsed by protomaps. */
  key: 'stands' | 'trupuri';
  /** URL of the .pmtiles bundle (relative paths work for local dev). */
  url: string;
  /** Vector-tile layer name as baked by tippecanoe `-l`. */
  dataLayer: string;
  /** Fill color (hex). */
  fill: string;
  /** Fill opacity 0..1. */
  fillOpacity: number;
  /** Stroke color (hex). */
  stroke: string;
  /** Stroke width in CSS pixels. */
  strokeWidth: number;
  /** Lowest zoom level at which to paint anything (paint rule lower bound). */
  minZoom: number;
  /** Stroke kicks in only past this zoom; below, just the fill paints. */
  strokeFromZoom: number;
  /**
   * Highest zoom at which the paint rules apply. Should be set well
   * above any reasonable user zoom (e.g. 24) so the layer never
   * "disappears" when the user zooms in past `maxDataZoom`. Distinct
   * from `maxDataZoom`, which is the bake limit fed to protomaps for
   * over-zoom calculations.
   */
  paintMaxZoom: number;
  /**
   * Maximum zoom level baked into the .pmtiles file. protomaps uses
   * this to know when to stop fetching tiles and start re-rendering
   * the deepest baked tile (over-zoom). Mismatching this with the
   * actual bake limit produces blank tiles past it.
   */
  maxDataZoom: number;
  /** Popup HTML builder for click handling. */
  popupBuilder: (props: IcasFeatureProperties) => string;
};

type Props = {
  config: IcasPmtilesConfig;
  enabled: boolean;
};

type PmFeature = {
  props?: IcasFeatureProperties;
  geom?: unknown;
};

type PmLayer = L.Layer & {
  remove: () => void;
  queryFeatures?: (lat: number, lng: number, displayZoom?: number) => PmFeature[];
};

type LeafletPmModule = {
  leafletLayer: (opts: Record<string, unknown>) => PmLayer;
  PolygonSymbolizer?: new (opts: Record<string, unknown>) => unknown;
  LineSymbolizer?: new (opts: Record<string, unknown>) => unknown;
};

const moduleCache: { promise: Promise<LeafletPmModule> | null } = { promise: null };

/**
 * Cache of per-URL availability so we only HEAD-probe each pmtiles
 * source once per session, regardless of how many times the layer
 * mounts/unmounts.
 */
const availabilityCache = new Map<string, boolean>();

/**
 * Verify a .pmtiles URL is actually serving binary data, not the SPA's
 * `index.html` fallback. On Vercel, missing static files get rewritten
 * to the React app's HTML — the PMTiles client would read those bytes
 * and throw "Wrong magic number for PMTiles archive". HEAD-probe the
 * URL: real PMTiles files come back as `application/octet-stream` (or
 * sometimes `application/x-protobuf`); the SPA fallback is `text/html`.
 *
 * Resolves true on success, false on any error or wrong content type.
 * The result is memoised in `availabilityCache` so repeated mounts
 * don't repeat the network call.
 */
async function probePmtilesAvailability(url: string): Promise<boolean> {
  const cached = availabilityCache.get(url);
  if (cached !== undefined) return cached;
  try {
    const resp = await fetch(url, { method: 'HEAD' });
    if (!resp.ok) {
      availabilityCache.set(url, false);
      return false;
    }
    const ct = (resp.headers.get('content-type') ?? '').toLowerCase();
    // Reject html / text — those are the SPA fallback or an error page.
    // Anything else (octet-stream, x-protobuf, application/vnd.*) is
    // acceptable: we don't enforce a specific mime because hosts vary
    // (Vercel, R2, S3, local dev server all return different values).
    const looksHtml = ct.includes('text/html') || ct.includes('text/plain');
    const ok = !looksHtml;
    availabilityCache.set(url, ok);
    return ok;
  } catch {
    availabilityCache.set(url, false);
    return false;
  }
}

/**
 * Lazy-load the protomaps-leaflet runtime on first use. Vite pre-bundles
 * the dep into `.vite/deps/` on first reference; main bundle stays clean
 * for users who never toggle a PMTiles layer.
 */
function loadProtomaps(): Promise<LeafletPmModule> {
  if (moduleCache.promise) return moduleCache.promise;
  moduleCache.promise = import('protomaps-leaflet') as unknown as Promise<LeafletPmModule>;
  return moduleCache.promise;
}

function buildPaintRules(mod: LeafletPmModule, config: IcasPmtilesConfig): unknown[] {
  const PolySym = mod.PolygonSymbolizer;
  const LineSym = mod.LineSymbolizer;

  const fillSym = PolySym
    ? new PolySym({ fill: config.fill, opacity: config.fillOpacity })
    : { fill: config.fill, opacity: config.fillOpacity };
  const strokeSym = LineSym
    ? new LineSym({ color: config.stroke, width: config.strokeWidth })
    : { color: config.stroke, width: config.strokeWidth };

  return [
    {
      dataLayer: config.dataLayer,
      symbolizer: fillSym,
      minzoom: config.minZoom,
      maxzoom: config.paintMaxZoom,
    },
    {
      dataLayer: config.dataLayer,
      symbolizer: strokeSym,
      minzoom: config.strokeFromZoom,
      maxzoom: config.paintMaxZoom,
    },
  ];
}

/**
 * Generic PMTiles vector-tile overlay for ICAS layers. Used by both
 * stands and trupuri.
 *
 * Why the `<LayerGroup>` wrapper: react-leaflet's `LayersControl.Overlay`
 * tracks its child via the layer-container context that built-in components
 * (`<GeoJSON>`, `<TileLayer>`, `<LayerGroup>`) hook into automatically.
 * A bare `useEffect` + `layer.addTo(map)` bypasses that context — the
 * overlay row gets skipped by the LayersControl widget entirely
 * (observed: only `entitati` showed up in the top-right widget when
 * stands/trupuri were `useMap`-based). Routing the PMTiles layer through
 * a `<LayerGroup>` registers properly and the row appears.
 *
 * Click-to-popup is handled via the layer's `queryFeatures` helper. If
 * the running protomaps-leaflet build doesn't expose it, the click is
 * silently ignored and the layer still renders.
 */
export function IcasPmtilesLayer({ config, enabled }: Props) {
  const map = useMap();
  const groupRef = useRef<L.LayerGroup>(null);

  // Availability of the .pmtiles URL. `null` while probing; `true` if
  // the URL serves a real PMTiles archive; `false` if it 404s or
  // returns the SPA fallback HTML (common on Vercel for missing
  // static files — see `stands.pmtiles` on production, which is
  // gitignored at 84 MB pending R2 upload). When false we render
  // nothing — the LayersControl row disappears so users don't see a
  // broken toggle.
  const [available, setAvailable] = useState<boolean | null>(
    () => availabilityCache.get(config.url) ?? null,
  );

  useEffect(() => {
    let cancelled = false;
    if (available !== null) return;
    probePmtilesAvailability(config.url).then((ok) => {
      if (cancelled) return;
      setAvailable(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [available, config.url]);

  // Create the ICAS pane synchronously after commit, *before* the child
  // <LayerGroup>'s own effect attaches a Leaflet layer using it. With a
  // plain useEffect, React would commit the LayerGroup child first;
  // Leaflet would resolve `pane: ICAS_PANE` to `undefined` and throw
  // when calling `pane.appendChild(...)`. Idempotent (the helper checks
  // for an existing pane).
  useLayoutEffect(() => {
    if (map) ensureIcasPane(map);
  }, [map]);

  useEffect(() => {
    if (!enabled) return;
    if (available !== true) return;
    let mounted = true;
    let pmLayer: PmLayer | null = null;
    let clickHandler: ((e: L.LeafletMouseEvent) => void) | null = null;

    loadProtomaps()
      .then((mod) => {
        if (!mounted) return;
        const paintRules = buildPaintRules(mod, config);
        pmLayer = mod.leafletLayer({
          url: config.url,
          paint_rules: paintRules,
          paintRules,
          maxDataZoom: config.maxDataZoom,
          attribution: '<a href="https://icas.md">ICAS Moldova</a>',
          pane: ICAS_PANE,
        });

        // Add to the LayerGroup we rendered below — the group is the
        // registered overlay child of LayersControl.Overlay. When the
        // user unchecks the overlay, LayersControl removes the
        // LayerGroup from the map (which removes our PMTiles layer
        // along with it). Falling back to `addTo(map)` if the ref
        // isn't ready yet keeps the layer visible if the timing is
        // unusual.
        const group = groupRef.current;
        if (group) {
          group.addLayer(pmLayer);
        } else {
          pmLayer.addTo(map);
        }

        clickHandler = (e: L.LeafletMouseEvent) => {
          const q = pmLayer?.queryFeatures;
          if (typeof q !== 'function') return;
          const features = q.call(pmLayer, e.latlng.lat, e.latlng.lng);
          if (!features || features.length === 0) return;
          const first = features[0];
          if (!first?.props) return;
          L.popup({ maxWidth: 320 })
            .setLatLng(e.latlng)
            .setContent(config.popupBuilder(first.props))
            .openOn(map);
        };
        map.on('click', clickHandler);
      })
      .catch((err: unknown) => {
        // eslint-disable-next-line no-console
        console.warn(`[ICAS] ${config.key} layer disabled:`, err);
      });

    return () => {
      mounted = false;
      if (clickHandler) map.off('click', clickHandler);
      if (pmLayer) {
        const group = groupRef.current;
        if (group) group.removeLayer(pmLayer);
        else pmLayer.remove();
      }
    };
  }, [enabled, map, config, available]);

  // If the probe came back negative, render nothing — the LayersControl
  // widget will skip the row entirely instead of showing a toggle that
  // would throw "Wrong magic number" on click. Probing-in-progress
  // (`available === null`) also renders nothing, but the cache means
  // a remount of the same URL skips the probe and resolves
  // synchronously, so we don't flicker.
  if (available === false) return null;

  // The empty LayerGroup is the registered child of LayersControl.Overlay.
  // We add the actual PMTiles layer to it imperatively above once protomaps
  // has loaded. Without this wrapper, LayersControl wouldn't display the
  // checkbox row at all.
  return <LayerGroup ref={groupRef} />;
}
